// Chemin: C:\gestion-copro\src\constants\aideContextuelle.tsx
// Session 136 : Configurations d'aide contextuelle par module VIGIE
// Session 235 : suppression mot "signalement" dans AIDE_DOSSIERS
// Contenu métier centralisé — chaque page n'a qu'à importer sa config

import React from 'react';
import {
  Mail,
  Users,
  AlertTriangle,
  FolderOpen,
  FileText,
  Shield,
  Plus,
  Sparkles,
  Send,
  Search,
  Camera,
  Eye,
  Banknote,
  Home,
  Calculator,
  TrendingUp,
  CheckCircle,
  BookOpen,
} from 'lucide-react';
import { AideContextuelleConfig } from '../components/ui/AideContextuelleModule';

// ============================================
// CORRESPONDANCES (Courriers / Emails / RAR)
// ============================================

export const AIDE_COURRIERS: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans tes correspondances',
  sousTitre: 'Tous tes échanges officiels avec syndic, promoteur et prestataires',
  colonnes: [
    {
      titre: 'Pourquoi cette page ?',
      contenu: (
        <p>
          En copropriété, un échange oral ne vaut rien juridiquement.
          Cette page centralise tous tes courriers, emails et recommandés
          pour constituer une trace écrite incontestable en cas de litige.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>SENTINELLE rédige :</strong> décris ta situation et l'IA génère un courrier professionnel avec les bons articles de loi.</span>
          </p>
          <p className="flex items-start gap-2">
            <Send className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Tu contrôles :</strong> relis, modifie si besoin, puis confirme l'envoi. Rien ne part sans ta validation.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'SENTINELLE veille',
      contenu: (
        <p>
          Chaque correspondance est analysée par SENTINELLE (méthode SCUD) pour évaluer
          le niveau de risque juridique et te recommander le bon canal d'envoi :
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-medium">email</span>
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">courrier</span>
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-xs font-medium">RAR</span>
        </p>
      ),
    },
  ],
  iconeCompact: <Mail className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Mes correspondances</strong> — Demande à SENTINELLE de rédiger tes courriers, emails et recommandés.
      Chaque envoi est analysé (méthode SCUD) et archivé automatiquement dans ta GED.
    </p>
  ),
};

// ============================================
// CONTACTS (Interlocuteurs)
// ============================================

export const AIDE_CONTACTS: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans ton carnet de contacts',
  sousTitre: 'Tes interlocuteurs clés pour une copropriété bien gérée',
  colonnes: [
    {
      titre: 'Pourquoi cette page ?',
      contenu: (
        <p>
          Syndic, promoteur, assurance, avocat... En copropriété, tu échanges avec
          de nombreux interlocuteurs. Centralise leurs coordonnées ici pour que
          SENTINELLE puisse préparer tes correspondances en un clic.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Plus className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Ajoute tes contacts :</strong> nom, type (syndic, promoteur...), email, téléphone, adresse.</span>
          </p>
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>SENTINELLE les utilise :</strong> quand tu demandes un courrier, le destinataire est pré-rempli automatiquement.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Astuce',
      contenu: (
        <p>
          Renseigne au minimum le <strong>syndic</strong> et le <strong>promoteur</strong> (si VEFA).
          Ce sont tes interlocuteurs les plus fréquents. Sans email, SENTINELLE ne pourra
          pas préparer d'envoi par email — seulement des courriers postaux.
        </p>
      ),
    },
  ],
  iconeCompact: <Users className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Mes contacts</strong> — Ajoute tes interlocuteurs (syndic, promoteur, assurance...) pour que
      SENTINELLE puisse pré-remplir automatiquement tes correspondances.
    </p>
  ),
};

// ============================================
// SIGNALEMENTS
// ============================================

export const AIDE_SIGNALEMENTS: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans tes signalements',
  sousTitre: 'Documente chaque problème pour protéger tes droits',
  colonnes: [
    {
      titre: 'Pourquoi signaler ?',
      contenu: (
        <p>
          Un désordre non signalé par écrit est un désordre qui n'existe pas juridiquement.
          Chaque signalement crée une trace datée avec photos, description et localisation
          — indispensable pour tes garanties constructeur et vos échanges avec le syndic.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Camera className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Documente :</strong> titre, description, catégorie, localisation et photos du désordre.</span>
          </p>
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>SENTINELLE agit :</strong> à partir de ton signalement, l'IA peut générer un courrier de mise en demeure ou de relance.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Suivi simplifié',
      contenu: (
        <p>
          Trois statuts suffisent :
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Ouvert</span>
          quand le problème est constaté,
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Courrier envoyé</span>
          quand tu as agi, et
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Résolu</span>
          quand c'est réglé.
        </p>
      ),
    },
  ],
  iconeCompact: <AlertTriangle className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Mes signalements</strong> — Documente chaque désordre avec photos et localisation.
      SENTINELLE peut ensuite rédiger un courrier de mise en demeure à partir de ton signalement.
    </p>
  ),
};

// ============================================
// DOSSIERS (Affaires)
// ============================================

export const AIDE_DOSSIERS: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans tes dossiers',
  sousTitre: 'Regroupe tes problèmes, documents et courriers par sujet',
  colonnes: [
    {
      titre: 'Pourquoi des dossiers ?',
      contenu: (
        <p>
          Un problème en copropriété génère souvent plusieurs courriers et documents.
          Un dossier les regroupe pour que tu aies une vue complète de chaque sujet
          — essentiel si les choses traînent ou finissent devant un tribunal.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Crée un dossier :</strong> donne-lui un titre, un type (malfaçon, litige, travaux...) et une priorité.</span>
          </p>
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>Liaison automatique :</strong> quand SENTINELLE génère un courrier lié à un dossier, il y est rattaché automatiquement.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Chronologie traçable',
      contenu: (
        <p>
          Chaque dossier affiche une chronologie complète : date d'ouverture,
          courriers envoyés, réponses reçues, documents archivés. C'est ton historique
          juridique, prêt à être présenté en AG ou à un avocat.
        </p>
      ),
    },
  ],
  iconeCompact: <FolderOpen className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Mes dossiers</strong> — Regroupe tes problèmes, courriers et documents par sujet.
      La chronologie de chaque dossier constitue ton historique juridique traçable.
    </p>
  ),
};

// ============================================
// DOCUMENTS (GED)
// ============================================

export const AIDE_DOCUMENTS: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans ta GED personnelle',
  sousTitre: 'Tous tes documents de copropriété classés et sécurisés',
  colonnes: [
    {
      titre: 'Pourquoi cette GED ?',
      contenu: (
        <p>
          PV d'AG, appels de fonds, diagnostics, contrats... En copropriété, les documents
          s'accumulent vite. Ta GED les classe automatiquement par catégorie pour que tu
          retrouves n'importe quel document en quelques secondes.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>Classement IA :</strong> utilise le bouton <strong>« Déposer dans SENTINELLE »</strong> en haut à droite — l'IA identifie automatiquement le type et la catégorie de ton document.</span>
          </p>
          <p className="flex items-start gap-2">
            <Search className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Vue par catégorie :</strong> Juridique, Financier, Technique, Correspondance... chaque document à sa place.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Archivage automatique',
      contenu: (
        <p>
          Les courriers envoyés, emails archivés et documents analysés par SENTINELLE
          atterrissent automatiquement dans ta GED avec la bonne catégorie.
          Tu n'as rien à faire — tout est tracé.
        </p>
      ),
    },
  ],
  iconeCompact: <FileText className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Mes documents</strong> — Dépose tes documents et SENTINELLE les classe automatiquement.
      Les courriers envoyés et emails archivés atterrissent ici sans effort.
    </p>
  ),
};

// ============================================
// DASHBOARD (Tableau de bord)
// ============================================

export const AIDE_DASHBOARD: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue sur ton tableau de bord',
  sousTitre: 'Ta copropriété en un coup d\'œil — ce qui compte, ce qui urge',
  colonnes: [
    {
      titre: 'Que vois-tu ici ?',
      contenu: (
        <p>
          Ton tableau de bord rassemble les infos essentielles : ton bien (lots, tantièmes, quote-part),
          tes charges dues au syndic, l'état de tes garanties constructeur et la complétude de ton dossier.
          C'est ton cockpit de suivi.
        </p>
      ),
    },
    {
      titre: 'Les alertes à surveiller',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Banknote className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <span><strong>Charges impayées :</strong> le bandeau rouge t'indique ce que tu dois au syndic. Clique pour voir le détail et marquer tes paiements.</span>
          </p>
          <p className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Garanties constructeur :</strong> GPA, biennale, décennale — le compte à rebours (J-XXX) te montre l'urgence. Agis avant expiration.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Complète ton dossier',
      contenu: (
        <p>
          Le pourcentage de complétude en bas de page liste les documents essentiels
          que SENTINELLE attend pour te protéger au mieux : PV d'AG, règlement, appels de fonds...
          Plus ton dossier est complet, plus l'IA est efficace.
        </p>
      ),
    },
  ],
  iconeCompact: <Eye className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Tableau de bord</strong> — Tes charges, garanties constructeur et complétude documentaire en un coup d'œil.
      Les alertes rouges nécessitent ton attention prioritaire.
    </p>
  ),
};

// ============================================
// COMPTABILITÉ (Vérification comptable)
// ============================================

export const AIDE_COMPTABILITE: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans ta vérification comptable',
  sousTitre: 'Contrôle tes appels de fonds, détecte les anomalies et gère ta fiscalité bailleur',
  colonnes: [
    {
      titre: 'Pourquoi vérifier ?',
      contenu: (
        <p>
          Les appels de fonds peuvent contenir des erreurs : mauvaise clé de répartition,
          charges non justifiées, régularisations contestables. SENTINELLE compare tes documents
          et attribue un score de conformité de 0 à 100 pour chaque exercice.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Calculator className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span><strong>Crée une vérification :</strong> choisis l'exercice (2025 ou 2025-2026) et ajoute tes appels de fonds, budget et annexes.</span>
          </p>
          <p className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>SENTINELLE analyse :</strong> montants appelés vs réglés, anomalies détectées, fiscalité bailleur (charges récupérables, TEOM).</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'Le score de conformité',
      contenu: (
        <p>
          Chaque vérification reçoit un score de <strong>0 à 100</strong> :
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">≥ 80 — Conforme</span>
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">60–79 — À surveiller</span>
          <span className="inline-block mx-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">{'<'} 60 — Anomalie</span>
          Ce score t'indique si tu dois interroger ton syndic.
        </p>
      ),
    },
  ],
  iconeCompact: <Calculator className="w-4 h-4 text-blue-600" />,
  texteCompact: (
    <p>
      <strong>Ma comptabilité</strong> — Dépose tes appels de fonds et SENTINELLE calcule un score de conformité,
      détecte les anomalies et identifie tes charges récupérables en tant que bailleur.
    </p>
  ),
};

// ============================================
// SUIVI BAILLEUR
// ============================================

export const AIDE_SUIVI_BAILLEUR: AideContextuelleConfig = {
  titreAccueil: 'Bienvenue dans ton suivi bailleur',
  sousTitre: "Loyers, charges et soldes de tes locataires en un coup d'œil",
  colonnes: [
    {
      titre: 'Pourquoi ce module ?',
      contenu: (
        <p>
          En tant que propriétaire bailleur en copropriété, tu dois jongler entre
          les loyers de ton locataire et les charges de ta copropriété. Ce module
          centralise tout : encaissements, retards, soldes — et te prépare à
          régulariser les charges annuellement.
        </p>
      ),
    },
    {
      titre: 'Comment ça marche ?',
      contenu: (
        <div className="space-y-1.5">
          <p className="flex items-start gap-2">
            <Home className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Ajoute ton locataire :</strong> loyer, provision charges, date d'entrée. Le suivi mensuel démarre automatiquement.</span>
          </p>
          <p className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span><strong>Enregistre les paiements :</strong> loyer, charges, régularisation. Le solde se met à jour en temps réel.</span>
          </p>
        </div>
      ),
    },
    {
      titre: 'SENTINELLE et la fiscalité',
      contenu: (
        <p>
          Les charges récupérables sur ton locataire (TEOM, entretien parties communes, etc.)
          sont calculées par SENTINELLE dans le module <strong>Ma comptabilité</strong>,
          à partir de tes appels de fonds et de tes tantièmes. Un gain de temps considérable
          à chaque régularisation annuelle.
        </p>
      ),
    },
  ],
  iconeCompact: <Home className="w-4 h-4 text-amber-500" />,
  texteCompact: (
    <p>
      <strong>Suivi bailleur</strong> — Enregistre les paiements de ton locataire et suis les soldes en temps réel.
      SENTINELLE calcule tes charges récupérables pour la régularisation annuelle.
    </p>
  ),
};
